import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Select, Field } from '@/components/ui/Field';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { MediaField } from '@/components/forms/MediaField';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { InfluencerRow } from '@/lib/admin-queries';

const PUBLISH = ['draft', 'published', 'archived'] as const;

interface Props {
  value: InfluencerRow;
  submitting: boolean;
  onSubmit: (v: InfluencerRow) => void | Promise<void>;
  onCancel: () => void;
}

const influencerSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only.'),
  name: z.string().min(2, 'Name is required.'),
  status: z.enum(PUBLISH),
});

function isValidUrl(s: string | null | undefined): boolean {
  if (!s) return true;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function InfluencerEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const [v, setV] = useState<InfluencerRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function patch<K extends keyof InfluencerRow>(key: K, val: InfluencerRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onNameBlur() {
    if (!v.slug) patch('slug', slugify(v.name));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const parsed = influencerSchema.safeParse(v);
    const next: Record<string, string> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
    }
    for (const f of ['tiktok_url', 'instagram_url', 'youtube_url', 'facebook_url'] as const) {
      if (!isValidUrl(v[f])) next[f] = 'Must be a valid http(s) URL.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="influencer-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="name" label="Name" required error={errors.name}>
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.name}
              onChange={(e) => patch('name', e.target.value)}
              onBlur={onNameBlur}
            />
          )}
        </Field>
        <Field id="slug" label="Slug" required error={errors.slug} hint="Lowercase letters, numbers, hyphens.">
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.slug}
              onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="headline" label="Headline" hint="e.g. Actor · Host">
          {({ id }) => (
            <Input id={id} value={v.headline ?? ''} onChange={(e) => patch('headline', e.target.value)} />
          )}
        </Field>
        <Field id="joined_at" label="Joined">
          {({ id }) => (
            <Input
              id={id}
              type="date"
              value={v.joined_at ?? ''}
              onChange={(e) => patch('joined_at', e.target.value || null)}
            />
          )}
        </Field>
      </div>

      <Field id="portrait" label="Portrait image" hint="Square crop recommended.">
        <MediaField value={v.portrait_media_id} onChange={(m) => patch('portrait_media_id', m)} bucket="public" />
      </Field>

      <Field id="bio_md" label="Short bio">
        <MarkdownField value={v.bio_md ?? ''} onChange={(s) => patch('bio_md', s)} rows={6} />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="tiktok_url" label="TikTok URL" error={errors.tiktok_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.tiktok_url ?? ''}
              onChange={(e) => patch('tiktok_url', e.target.value || null)}
              placeholder="https://tiktok.com/@handle"
            />
          )}
        </Field>
        <Field id="instagram_url" label="Instagram URL" error={errors.instagram_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.instagram_url ?? ''}
              onChange={(e) => patch('instagram_url', e.target.value || null)}
              placeholder="https://instagram.com/handle"
            />
          )}
        </Field>
        <Field id="youtube_url" label="YouTube URL" error={errors.youtube_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.youtube_url ?? ''}
              onChange={(e) => patch('youtube_url', e.target.value || null)}
              placeholder="https://youtube.com/@handle"
            />
          )}
        </Field>
        <Field id="facebook_url" label="Facebook URL" error={errors.facebook_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.facebook_url ?? ''}
              onChange={(e) => patch('facebook_url', e.target.value || null)}
              placeholder="https://facebook.com/yourpage"
            />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field id="status" label="Publish" required>
          {({ id }) => (
            <Select
              id={id}
              value={v.status}
              onChange={(e) => patch('status', e.target.value as InfluencerRow['status'])}
            >
              {PUBLISH.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <Checkbox
            checked={v.is_active}
            onCheckedChange={(c) => patch('is_active', !!c)}
            id="is_active"
          />
          <span>Active</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="influencer-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create influencer'}
        </Button>
      </div>
    </form>
  );
}
